// src/utils/compatibility/browserDetection.js

/**
 * Browser context detection for DI-0076.
 * We treat "unsupported" as:
 *  - In-app browsers (Instagram/Facebook/etc.)
 *  - Android WebView (wv)
 *  - Safari / non-Chromium WebKit contexts (common issue for QR/deeplinks)
 *
 * Traceability: F005-DI-0077
 */

function uaIncludesAny(ua, tokens) {
  return tokens.some((t) => ua.includes(t));
}

export function detectBrowserContext() {
  const ua = (navigator.userAgent || "").toLowerCase();

  // Common in-app browser tokens
  const inAppTokens = [
    "fban", // Facebook
    "fbav", // Facebook
    "instagram",
    "line/",
    "twitter",
    "tiktok",
    "snapchat",
    "pinterest",
    "linkedinapp",
  ];

  const isInAppBrowser = uaIncludesAny(ua, inAppTokens);

  // Android WebView commonly contains "; wv" or "version/x.x"
  const isAndroidWebView =
    ua.includes(" wv") || (ua.includes("android") && ua.includes("version/"));

  // Identify major browsers
  const isEdge = ua.includes("edg/");
  const isOpera = ua.includes("opr/") || ua.includes("opera");
  const isFirefox = ua.includes("firefox/");
  const isChromeLike =
    (ua.includes("chrome/") || ua.includes("chromium/")) && !isEdge && !isOpera;

  // Safari heuristic: has "safari" but not chrome-like / edge / opera / firefox
  // Safari is now supported
  const isSafari =
    ua.includes("safari") && !isChromeLike && !isEdge && !isOpera && !isFirefox;

  // Chromium-based supported bucket: Chrome-like, Edge, Opera (Chromium), others with Chromium token
  const isSupportedByName =
    isChromeLike || isEdge || isOpera || isFirefox || isSafari;

  const reasons = [];
  if (isInAppBrowser) reasons.push("in_app_browser");
  if (isAndroidWebView) reasons.push("webview");

  const isSupported = isSupportedByName && reasons.length === 0;

  return {
    ua,
    isSupported,
    reasons, // array of strings
    browser: isEdge
      ? "Edge"
      : isOpera
      ? "Opera"
      : isFirefox
      ? "Firefox"
      : isChromeLike
      ? "Chrome/Chromium"
      : isSafari
      ? "Safari"
      : "Unknown",
    context: isInAppBrowser
      ? "in_app"
      : isAndroidWebView
      ? "webview"
      : "browser",
  };
}
